import React, { useEffect, useState } from "react";
import { PageContainer, PageHeader, Card, ConfirmDelete } from "./Shared";
import { adminApi } from "./api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, PencilSimple, Trash, Warning, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import ImageUpload from "./ImageUpload";
import { runValidators, validators } from "./validators";

const empty = { title: "", subtitle: "", image_id: null, cta_text: "", cta_link: "", order: 0, status: "published" };

export default function SliderManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const load = async () => setItems((await adminApi.get("/admin/slides")).data);
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startCreate = () => { setEditing(null); setForm(empty); setAgreed(false); setOpen(true); };
  const startEdit = (s) => { setEditing(s); setForm({ ...empty, ...s }); setAgreed(true); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("Slide title required");
    if (form.cta_link) {
      const urlErr = !form.cta_link.startsWith("#") && runValidators(form.cta_link, [validators.url]);
      if (urlErr) return toast.error(`CTA link: ${urlErr} (or use an in-page anchor like #products)`);
    }
    if (!agreed) return toast.error("Please confirm the image rights checkbox before saving");
    setBusy(true);
    try {
      if (editing) await adminApi.put(`/admin/slides/${editing.id}`, form);
      else await adminApi.post("/admin/slides", form);
      toast.success(editing ? "Updated" : "Slide added");
      setOpen(false);
      await load();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    await adminApi.delete(`/admin/slides/${id}`);
    await load();
    toast.success("Deleted");
  };

  return (
    <PageContainer>
      <PageHeader
        title="Hero Slider"
        subtitle="Auto-rotating banner slides on the public homepage. Order is left-to-right; first slide shows on load."
        action={<Button onClick={startCreate} className="text-white" style={{ background: "#1B6FE0" }} data-testid="slide-new"><Plus size={14} weight="bold" /> New slide</Button>}
      />

      <Card className="p-4 mb-4 bg-blue-50/60 border-blue-100" data-testid="slider-guidelines">
        <div className="flex items-start gap-3">
          <Warning size={18} weight="duotone" style={{ color: "#1B6FE0" }} className="mt-0.5 shrink-0" />
          <div className="text-[12.5px] text-[#0F2A6E] leading-relaxed">
            <div className="font-semibold mb-1">Image guidelines</div>
            <ul className="list-disc ml-4 space-y-0.5">
              <li><b>Resolution</b>: 1920×800 or larger (16:9 to 21:9 aspect ratio)</li>
              <li><b>Format</b>: PNG, JPG or WebP — under 5 MB (server accepts 64×64 up to 6000×6000)</li>
              <li><b>Quality</b>: sharp, no compression artifacts, no watermarks</li>
              <li><b>Copyright</b>: you must own the rights or have a commercial license — public stock sites like Unsplash / Pexels work</li>
            </ul>
          </div>
        </div>
      </Card>

      <Card>
        <ul className="divide-y divide-slate-100" data-testid="slide-list">
          {items.length === 0 && <li className="p-8 text-center text-[#6B7A8D]">No slides yet — add one to enable the auto-rotating banner.</li>}
          {items.map((s) => (
            <li key={s.id} className="p-4 flex items-center gap-3" data-testid={`slide-item-${s.id}`}>
              <div className="w-20 h-12 rounded-md bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
                <SlidePreview imageId={s.image_id} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[14.5px] truncate" style={{ color: "#0F2A6E" }}>{s.title}</div>
                <div className="text-[12px] text-[#6B7A8D] truncate">{s.subtitle || "—"} · order {s.order} · <span className={s.status === "published" ? "text-emerald-600" : "text-amber-600"}>{s.status}</span></div>
              </div>
              <Button variant="outline" size="sm" onClick={() => startEdit(s)} data-testid={`slide-edit-${s.id}`}><PencilSimple size={13} weight="bold" /></Button>
              <ConfirmDelete title="Delete this slide?" description={`"${s.title}" will be removed from the hero banner.`} onConfirm={() => del(s.id)} testId={`slide-del-${s.id}`}>
                <Button variant="outline" size="sm" className="text-[#1B6FE0] hover:bg-blue-50 hover:text-[#1B6FE0]" data-testid={`slide-del-${s.id}`}><Trash size={13} weight="bold" /></Button>
              </ConfirmDelete>
            </li>
          ))}
        </ul>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="slide-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit slide" : "New slide"}</DialogTitle>
            <DialogDescription>Slides auto-rotate every 5 seconds on the public homepage.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input className="mt-1.5" value={form.title} onChange={(e) => set("title", e.target.value)} required data-testid="slide-title" />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Textarea rows={2} className="mt-1.5" value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} data-testid="slide-subtitle" />
            </div>
            <ImageUpload value={form.image_id} onChange={(id) => set("image_id", id)} label="Background image (recommended 1920×800)" testId="slide-image" />
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>CTA button text</Label>
                <Input className="mt-1.5" placeholder="Explore brands" value={form.cta_text} onChange={(e) => set("cta_text", e.target.value)} data-testid="slide-cta-text" />
              </div>
              <div>
                <Label>CTA link (URL or #anchor)</Label>
                <Input className="mt-1.5" placeholder="#products or https://…" value={form.cta_link} onChange={(e) => set("cta_link", e.target.value)} data-testid="slide-cta-link" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="w-[160px]" data-testid="slide-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft (hidden)</SelectItem>
                </SelectContent>
              </Select>
              <Label className="ml-3">Order</Label>
              <Input type="number" className="w-24" value={form.order} onChange={(e) => set("order", parseInt(e.target.value || "0", 10))} />
            </div>

            <label className="flex items-start gap-2 text-[12.5px] text-[#1C2B3A] cursor-pointer mt-2" data-testid="slide-copyright">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5" />
              <span>I confirm this image is mine, royalty-free, or licensed for commercial use — and meets the resolution / quality guidelines above.</span>
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ background: "#1B6FE0" }} data-testid="slide-save">
                {busy ? "Saving…" : (editing ? "Update slide" : "Add slide")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

function SlidePreview({ imageId }) {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    if (!imageId) return;
    let c = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/images/${imageId}`)
      .then((r) => r.json()).then((j) => !c && setSrc(j.data_url)).catch(() => {});
    return () => { c = true; };
  }, [imageId]);
  if (src) return <img src={src} alt="" className="w-full h-full object-cover" />;
  return <ImageIcon size={16} weight="duotone" className="text-slate-400" />;
}
